import { Children, useId, useState } from "react";
import {
  IconNameEnum,
  useHotelsStyles,
  useModuleBEM,
} from "@b2bportal/core-themes";
import {
  CoreHotelsComponent,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import { Amenity, MealPlanKind, type Lodging } from "@b2bportal/lodging-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { Dialog, DialogContent, Icon } from "@tenant/ui";
import clsx from "clsx";
import defaultStyles from "./Features.module.scss";

export type FeaturesProps = PlatformComponentProps & {
  hotel: Lodging;
};

export const Features = ({ hotel, className, children }: FeaturesProps) => {
  const { t } = useI18nContext();

  const styles = useHotelsStyles(
    CoreHotelsComponent.HotelCardFeatures,
    defaultStyles
  );
  const [block, cn] = useModuleBEM(
    styles,
    CoreHotelsComponent.HotelCardFeatures
  );

  const hasBreakfast = hotel.lodging.amenities.some(
    ({ amenity }) =>
      amenity === Amenity.BreakfastIncluded ||
      amenity === Amenity.FreeBreakfast ||
      amenity === Amenity.PaidBreakfast
  );

  const freeBreakfast = hotel.lodging.amenities.some(
    ({ amenity }) =>
      amenity === Amenity.FreeBreakfast || amenity === Amenity.BreakfastIncluded
  );

  const mealPlanKind = hotel.price?.mealPlan?.kind;
  const allInclusiveBreakfast = mealPlanKind === MealPlanKind.AllInclusive;

  const mealLabel = allInclusiveBreakfast
    ? t("core-hotels.availabilityPage.filters.mealPlan.kind.allInclusive")
    : freeBreakfast
      ? t(
          "core-hotels.availabilityPage.results.hotelCard.features.freeBreakfast"
        )
      : t(
          "core-hotels.availabilityPage.results.hotelCard.features.breakfastAvailable"
        );

  const { isFreeCancel } = hotel;
  const [freeCancelModalOpen, setFreeCancelModalOpen] = useState(false);
  const freeCancelDialogId = useId();

  const handleFreeCancelModalOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setFreeCancelModalOpen(true);
  };

  if (
    !hasBreakfast &&
    !allInclusiveBreakfast &&
    !isFreeCancel &&
    Children.count(children) === 0
  ) {
    return null;
  }

  return (
    <div className={clsx(block, className)}>
      {isFreeCancel && (
        <Dialog
          open={freeCancelModalOpen}
          onOpenChange={setFreeCancelModalOpen}
        >
          <button
            type="button"
            onClick={(e) => handleFreeCancelModalOpen(e)}
            className={cn("info-button")}
            aria-label={t(
              "core-hotels.availabilityPage.freeCancellation.infoAriaLabel"
            )}
            aria-haspopup="dialog"
            aria-expanded={freeCancelModalOpen}
            aria-controls={freeCancelDialogId}
            data-state={freeCancelModalOpen ? "open" : "closed"}
          >
            <span className={cn("free-cancellation")}>
              <Icon iconName={IconNameEnum.checkCircle} />
              {t(
                "core-hotels.availabilityPage.results.hotelCard.features.freeCancellation"
              )}
              <Icon iconName={IconNameEnum.info} />
            </span>
          </button>
          <DialogContent
            id={freeCancelDialogId}
            title={t(
              "core-hotels.availabilityPage.freeCancellation.modalTitle"
            )}
            className={cn("modal")}
          >
            <p className={cn("modal-description")}>
              {t(
                "core-hotels.availabilityPage.freeCancellation.modalDescription"
              )}
            </p>
          </DialogContent>
        </Dialog>
      )}
      {(hasBreakfast || allInclusiveBreakfast) && (
        <div className={cn("breakfast")}>
          <Icon iconName={IconNameEnum.hotel__breakfast} />
          {mealLabel}
        </div>
      )}
      {children}
    </div>
  );
};
